/**
 * Finding a service by typing part of its name.
 *
 * A counter that offers sixty services cannot offer them as a drop-down, and
 * the person searching is not always reading the interface in the language the
 * service was named in — a Georgian technician types `polish`, a Russian one
 * types `полир`. So every language the service carries is searched, along with
 * its slug and its category, and the words of the query may arrive in any
 * order: `мойка премиум` finds «Премиальная мойка».
 */
import type { Localized } from './types';

export interface SearchableService {
  slug: string;
  name: Localized;
  categoryName: Localized;
}

const fold = (text: string): string => text.toLowerCase().replace(/\s+/g, ' ').trim();

const haystack = (service: SearchableService): string => fold([
  service.name.ka, service.name.ru, service.name.en,
  service.categoryName.ka, service.categoryName.ru, service.categoryName.en,
  service.slug.replace(/-/g, ' '),
].join(' '));

export function matchesQuery(service: SearchableService, query: string): boolean {
  const words = fold(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;
  const text = haystack(service);
  return words.every((word) => text.includes(word));
}

export function filterServices<T extends SearchableService>(services: readonly T[], query: string): T[] {
  return services.filter((service) => matchesQuery(service, query));
}
